import styles from "../Styles/CookiesBanner.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCookieBite, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { useState, useEffect } from "react";
import { initGA, logEvent } from "../Analytic/Analytics";

// Tracking ID for analytics
const TRACKING_ID = "G-8GL1SYZL2C";

// Cookie consent ვადა - 1 კვირა
const CONSENT_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 დღე მილიწამებში
// const CONSENT_DURATION = 1 * 60 * 1000; // 1 წუთი (ტესტისთვის)

// TypeScript-ისთვის gtag ტიპის განსაზღვრა
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

function CookiesBanner() {
  const [isVisible, setIsVisible] = useState(true);
  const [cookieSettings, setCookieSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(true);

  // შევამოწმოთ cookies-ის სტატუსი და ვადა კომპონენტის ჩატვირთვისას
  useEffect(() => {
    const checkConsentExpiry = () => {
      const cookiesAccepted = localStorage.getItem("cookiesAccepted");
      const consentTimestamp = localStorage.getItem("consentTimestamp");

      if (!cookiesAccepted || !consentTimestamp) {
        // თუ არ არის შენახული, ბანერი უნდა ჩანდეს
        setIsVisible(true);
        return;
      }

      const now = Date.now();
      const timeElapsed = now - parseInt(consentTimestamp);

      if (timeElapsed > CONSENT_DURATION) {
        // ვადა გასულია - წავშალოთ ძველი მონაცემები და ვაჩვენოთ ბანერი
        localStorage.removeItem("cookiesAccepted");
        localStorage.removeItem("consentTimestamp");
        localStorage.removeItem("analyticsEnabled");
        localStorage.removeItem("marketingEnabled");
        setIsVisible(true);

        // console.log('Cookie consent expired - showing banner again');
      } else {
        // ვადა ჯერ არ გასულა - ბანერი არ უნდა ჩანდეს
        setIsVisible(false);

        // ჩავტვირთოთ არსებული პარამეტრები
        const savedAnalytics =
          localStorage.getItem("analyticsEnabled") === "true";
        const savedMarketing =
          localStorage.getItem("marketingEnabled") === "true";
        setAnalyticsEnabled(savedAnalytics);
        setMarketingEnabled(savedMarketing);

        // გამოვითვალოთ დარჩენილი დრო
        // const remainingTime = CONSENT_DURATION - timeElapsed;
        // const remainingDays = Math.ceil(remainingTime / (24 * 60 * 60 * 1000));
        // console.log(`Cookie consent valid for ${remainingDays} more days`);
      }
    };

    checkConsentExpiry();
  }, []);

  const handleAcceptAll = () => {
    const now = Date.now();

    // ყველა cookies-ის მიღება timestamp-ით
    localStorage.setItem("cookiesAccepted", "all");
    localStorage.setItem("consentTimestamp", now.toString());
    localStorage.setItem("analyticsEnabled", "true");
    localStorage.setItem("marketingEnabled", "true");

    // Analytics-ის ჩართვა
    initGA(TRACKING_ID);

    // Event-ის დალოგვა
    logEvent("Cookie Banner", "Accept All", "User accepted all cookies");

    setIsVisible(false);
  };

  const handleSettings = () => {
    setCookieSettings(true);

    // Event-ის დალოგვა
    logEvent("Cookie Banner", "Open Settings", "User opened cookie settings");
  };

  const handleBackToMain = () => {
    setCookieSettings(false);
  };

  const handleSaveSettings = () => {
    const now = Date.now();

    // პარამეტრების შენახვა timestamp-ით
    localStorage.setItem("cookiesAccepted", "custom");
    localStorage.setItem("consentTimestamp", now.toString());
    localStorage.setItem("analyticsEnabled", analyticsEnabled.toString());
    localStorage.setItem("marketingEnabled", marketingEnabled.toString());

    // Analytics-ის მართვა მომხმარებლის არჩევანის მიხედვით
    if (analyticsEnabled) {
      initGA(TRACKING_ID);
      logEvent(
        "Cookie Banner",
        "Custom Settings",
        `Analytics: ${analyticsEnabled}, Marketing: ${marketingEnabled}`
      );
    } else {
      // Analytics-ის გამორთვა (თუ უკვე ინიციალიზებულია)
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("consent", "update", {
          analytics_storage: "denied",
        });
      }
    }

    setIsVisible(false);
  };

  const handleClose = () => {
    // დახურვისას ყველა cookies-ის მიღება
    handleAcceptAll();
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookiesBannerOverlay}>
      <div className={styles.cookiesBannerContainer}>
        <div className={styles.cookiesBanner}>
          <div className={styles.cookiesBannerContent}>
            {/* დახურვის ღილაკი */}
            <button
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="ბანერის დახურვა"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {/* პარამეტრების კონტენტი */}
            {cookieSettings ? (
              <div className={styles.settingsView}>
                {/* უკან დაბრუნების ღილაკი */}
                <div className={styles.settingsHeader}>
                  <button
                    className={styles.backButton}
                    onClick={handleBackToMain}
                    aria-label="უკან დაბრუნება"
                  >
                    <FontAwesomeIcon icon={faCircleLeft} />
                  </button>
                  <h3 className={styles.settingsTitle}>🍪Cookie ფაილები</h3>
                </div>

                {/* კუკიების კატეგორიები */}
                <div className={styles.cookieCategories}>
                  {/* აუცილებელი კუკიები */}
                  <div className={styles.cookieCategory}>
                    <div className={styles.categoryHeader}>
                      <div className={styles.categoryInfo}>
                        <h4 className={styles.categoryTitle}>აუცილებელი</h4>
                        <p className={styles.categoryDescription}>
                          საჭიროა საიტის გამართული ფუნქციონირებისთვის. თანხმობის
                          გარეშე არ გექნებათ წვდომა ვებგვერდზე განთავსებულ
                          კონტენტზე
                        </p>
                      </div>
                      <div className={styles.toggleWrapper}>
                        <input
                          type="checkbox"
                          id="essential"
                          checked={true}
                          disabled={true}
                          className={styles.toggleInput}
                        />
                        <label
                          htmlFor="essential"
                          className={styles.toggleLabel}
                        >
                          <span className={styles.toggleSlider}></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* ანალიტიკური კუკიები */}
                  <div className={styles.cookieCategory}>
                    <div className={styles.categoryHeader}>
                      <div className={styles.categoryInfo}>
                        <h4 className={styles.categoryTitle}>ანალიტიკური</h4>
                        <p className={styles.categoryDescription}>
                          საიტის გამოყენების, სტატისტიკისა და მომხმარებლის
                          ქცევის ანალიზისთვის
                        </p>
                      </div>
                      <div className={styles.toggleWrapper}>
                        <input
                          type="checkbox"
                          id="analytics"
                          checked={analyticsEnabled}
                          onChange={(e) =>
                            setAnalyticsEnabled(e.target.checked)
                          }
                          className={styles.toggleInput}
                        />
                        <label
                          htmlFor="analytics"
                          className={styles.toggleLabel}
                        >
                          <span className={styles.toggleSlider}></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* მარკეტინგული კუკიები */}
                  <div className={styles.cookieCategory}>
                    <div className={styles.categoryHeader}>
                      <div className={styles.categoryInfo}>
                        <h4 className={styles.categoryTitle}>მარკეტინგული</h4>
                        <p className={styles.categoryDescription}>
                          გამოიყენება თქვენს ინტერესებზე მორგებული პროდუქტებისა
                          და სერვისების შესათავაზებლად
                        </p>
                      </div>
                      <div className={styles.toggleWrapper}>
                        <input
                          type="checkbox"
                          id="marketing"
                          checked={marketingEnabled}
                          onChange={(e) =>
                            setMarketingEnabled(e.target.checked)
                          }
                          className={styles.toggleInput}
                        />
                        <label
                          htmlFor="marketing"
                          className={styles.toggleLabel}
                        >
                          <span className={styles.toggleSlider}></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                {/* დასტურის ღილაკი */}
                <div className={styles.settingsActions}>
                  <button
                    className={styles.saveButton}
                    onClick={handleSaveSettings}
                  >
                    დადასტურება
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.contentWrapper}>
                <div className={styles.iconAndText}>
                  <FontAwesomeIcon
                    icon={faCookieBite}
                    className={styles.cookieIcon}
                  />
                  <div className={styles.textContent}>
                    <h3 className={styles.bannerTitle}>
                      Cookies-ის გამოყენება
                    </h3>
                    <p className={styles.bannerDescription}>
                      მომხმარებლის უსაფრთხოებისა და გამოცდილების გაუმჯობესების
                      მიზნით საიტი იყენებს Cookies. ბანერის დახურვით
                      ეთანხმებით მათ გამოყენებას.
                    </p>

                    <p className={styles.moreInfo}>
                       <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://xeloba.ge/cookies-policy"
                  >
                    <span>გაიგე მეტი</span>
                  </a>
                    </p>
                  </div>
                </div>

                <div className={styles.actionButtons}>
                  <button
                    className={styles.settingsButton}
                    onClick={handleSettings}
                  >
                    პარამეტრები
                  </button>
                  <button
                    className={styles.acceptButton}
                    onClick={handleAcceptAll}
                  >
                    ვეთანხმები
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiesBanner;


